import { useQuery } from '@tanstack/react-query';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { buildSuiteCounts } from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { useDashboardDateRange, type DashboardRange } from '@/features/dashboard/hooks';
import { querySuiteExecutions, querySuiteExecutionStatusSummary } from '@/features/executions/data';
import { buildChartDates, buildLineChartSeries } from '@/features/executions/utils/chartData';
import { buildStackedExecutionChartData } from '@/features/executions/utils/stackedExecutionChart';
import { buildMetricValueItems } from '@/features/metrics/components/metricValueItems';
import {
  buildStatusDeltaMap,
  buildStatusMetricCards,
  mapStatusOptionsToMetricOptions,
  resolveMetricTone,
} from '@/features/metrics/components/statusMetricCards';
import { useMetricsQuery } from '@/features/metrics/hooks/metrics';
import { buildThresholdGuideLines } from '@/features/metrics/lib/chartThresholdGuides';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter, useProjectMetricThresholds } from '@/features/projects';
import {
  buildSelectedDayRangeFilters,
  resolveChartDaySelection,
} from '@/features/shared/hooks/detailDateHelpers';
import {
  resolveFrequencyPerDay,
  resolvePassRateFromSummary,
  resolveTimestamp,
} from '@/features/suites/pages/suiteDetailMetrics';
import { suiteExecutionSummaryStatusOptions } from '@/shared/constants/statusOptions';
import { queryKeys, useAccessToken, usePersistedStringArray, useTagOptions } from '@/shared/hooks';
import {
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { SuiteDetailDto, SuiteExecutionStatusSummaryDto } from '@flowqa/shared';

type SuiteDetailPageViewModelParams = {
  suite: SuiteDetailDto | null | undefined;
};

type SuiteChartTab = 'status' | 'pass-rate' | 'frequency' | 'duration';
type SuiteDataTab = 'executions' | 'tests';

export type SuiteDetailPageViewModel = {
  title: string;
  suiteId: string;
  chartTab: SuiteChartTab;
  setChartTab: Dispatch<SetStateAction<SuiteChartTab>>;
  suppressChartAnimation: boolean;
  range: DashboardRange;
  setRange: Dispatch<SetStateAction<DashboardRange>>;
  selectedStartDay: string | null;
  setSelectedStartDay: Dispatch<SetStateAction<string | null>>;
  selectedEndDay: string | null;
  setSelectedEndDay: Dispatch<SetStateAction<string | null>>;
  isAllMode: boolean;
  pickerStartDay: string | null;
  pickerEndDay: string | null;
  handleStartDayChange: (nextDay: string | null) => void;
  handleEndDayChange: (nextDay: string | null) => void;
  suiteMetricCards: ReturnType<typeof buildStatusMetricCards>;
  metricValueItems: ReturnType<typeof buildMetricValueItems>;
  chartHeight: number;
  showChartDateClear: boolean;
  handleChartDateClear: () => void;
  stackedChartData: ReturnType<typeof buildStackedExecutionChartData>;
  passRateChartData: ReturnType<typeof buildLineChartSeries> | [] | null;
  frequencyChartData: ReturnType<typeof buildLineChartSeries> | [] | null;
  durationChartData: ReturnType<typeof buildLineChartSeries> | [] | null;
  passRateGuideLines: ReturnType<typeof buildThresholdGuideLines>;
  frequencyGuideLines: ReturnType<typeof buildThresholdGuideLines>;
  durationGuideLines: ReturnType<typeof buildThresholdGuideLines>;
  handleChartSelect: (dataIndex: number) => void;
  showLineChartPoints: boolean;
  dataTab: SuiteDataTab;
  setDataTab: Dispatch<SetStateAction<SuiteDataTab>>;
  tableDeltasEnabled: boolean;
  setTableDeltasEnabled: Dispatch<SetStateAction<boolean>>;
  showTableDeltaToggle: boolean;
  showTableDeltas: boolean;
  selectedTags: string[];
  tagOptions: Array<{ value: string; label: string }>;
  tagOptionsLoading: boolean;
  handleTagsChange: (next: string[]) => void;
  suiteExecutionFilters: ReturnType<typeof mergeFilters>;
};

const useSuiteDetailPageViewModelInternal = ({
  suite,
}: SuiteDetailPageViewModelParams): SuiteDetailPageViewModel => {
  const title = suite?.name ?? '';
  const suiteId = suite?.id ?? '';
  const [chartTab, setChartTab] = useState<'status' | 'pass-rate' | 'frequency' | 'duration'>(
    'status',
  );
  const [suppressChartAnimation, setSuppressChartAnimation] = useState(false);
  const [range, setRange] = useState<DashboardRange>('7d');
  const [selectedStartDay, setSelectedStartDay] = useState<string | null>(null);
  const [selectedEndDay, setSelectedEndDay] = useState<string | null>(null);
  const [tableDeltasEnabled, setTableDeltasEnabled] = useState(false);
  const [dataTab, setDataTab] = useState<'executions' | 'tests'>('executions');
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:suite-detail');
  const { rangeStartIso, rangeEndIso, previousRangeStartIso, previousRangeEndIso } =
    useDashboardDateRange(range);
  const accessToken = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { tagOptions, loading: tagOptionsLoading } = useTagOptions({
    projectId: selectedProjectId,
    enabled: Boolean(accessToken),
  });
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const isAllMode = range === 'all';
  const queryEnabled = Boolean(accessToken && suite && selectedProjectId);

  const dateRangeFilters = useMemo(
    () => buildDateRangeFilters('timestamp', rangeStartIso, rangeEndIso),
    [rangeEndIso, rangeStartIso],
  );
  const previousDateRangeFilters = useMemo(
    () => buildDateRangeFilters('timestamp', previousRangeStartIso, previousRangeEndIso),
    [previousRangeEndIso, previousRangeStartIso],
  );
  const baseSuiteFilters = useMemo(() => buildEqFilterValue('suiteId', suiteId), [suiteId]);
  const selectedDayFilters = useMemo(() => {
    return buildSelectedDayRangeFilters('timestamp', selectedStartDay, selectedEndDay);
  }, [selectedEndDay, selectedStartDay]);
  const tagFilters = useMemo(() => buildInFilter('tags', selectedTags), [selectedTags]);
  const suiteExecutionFilters = useMemo(
    () =>
      mergeFilters(
        baseSuiteFilters,
        selectedDayFilters,
        !isAllMode ? dateRangeFilters : undefined,
        tagFilters,
      ),
    [baseSuiteFilters, dateRangeFilters, isAllMode, selectedDayFilters, tagFilters],
  );
  const hasCustomDates = Boolean(selectedStartDay ?? selectedEndDay);
  const metricsRange = hasCustomDates ? 'all' : range;
  const metricsQuery = useMetricsQuery({
    range: metricsRange,
    statuses: [],
    scope: 'suite',
    projectId: selectedProjectId,
    entityId: suiteId,
    name: title,
    startDay: hasCustomDates ? selectedStartDay : null,
    endDay: hasCustomDates ? selectedEndDay : null,
    enabled: queryEnabled,
  });
  const previousStartDay = previousRangeStartIso?.slice(0, 10) ?? null;
  const previousEndDay = previousRangeEndIso?.slice(0, 10) ?? null;
  const showMetricDelta = Boolean(
    !isAllMode && !hasCustomDates && previousStartDay && previousEndDay,
  );
  const previousMetricsQuery = useMetricsQuery({
    range: 'all',
    statuses: [],
    scope: 'suite',
    projectId: selectedProjectId,
    entityId: suiteId,
    name: title,
    startDay: previousStartDay,
    endDay: previousEndDay,
    enabled: queryEnabled && showMetricDelta,
  });
  const earliestExecutionQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'suite-executions/earliest',
      suiteId,
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
    ),
    enabled: queryEnabled,
    queryFn: async () =>
      await querySuiteExecutions(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'timestamp', direction: 'asc' }],
          filters: baseSuiteFilters,
        },
        accessToken,
      ),
  });
  const latestExecutionQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'suite-executions/latest',
      suiteId,
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
    ),
    enabled: queryEnabled,
    queryFn: async () =>
      await querySuiteExecutions(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'timestamp', direction: 'desc' }],
          filters: baseSuiteFilters,
        },
        accessToken,
      ),
  });
  const metricsStartDay = useMemo(() => {
    const items = earliestExecutionQuery.data ? earliestExecutionQuery.data.items : [];
    if (items.length === 0) return null;
    const dateValue = resolveTimestamp(items[0].timestamp);
    return dateValue ? dateValue.toISOString().slice(0, 10) : null;
  }, [earliestExecutionQuery.data]);
  const metricsEndDay = useMemo(() => {
    const items = latestExecutionQuery.data ? latestExecutionQuery.data.items : [];
    if (items.length === 0) return null;
    const dateValue = resolveTimestamp(items[0].timestamp);
    return dateValue ? dateValue.toISOString().slice(0, 10) : null;
  }, [latestExecutionQuery.data]);
  const pickerStartDay = useMemo(() => {
    if (selectedStartDay) return selectedStartDay;
    if (!isAllMode) {
      return rangeStartIso ? rangeStartIso.slice(0, 10) : null;
    }
    return metricsStartDay;
  }, [isAllMode, metricsStartDay, rangeStartIso, selectedStartDay]);
  const pickerEndDay = useMemo(() => {
    if (selectedEndDay) return selectedEndDay;
    if (!isAllMode) {
      return rangeEndIso ? rangeEndIso.slice(0, 10) : null;
    }
    return metricsEndDay;
  }, [isAllMode, metricsEndDay, rangeEndIso, selectedEndDay]);
  const handleStartDayChange = useMemo(
    () => (nextDay: string | null) => {
      setSelectedStartDay(nextDay);
      if (!nextDay) {
        setSelectedEndDay(null);
        return;
      }
      if (!selectedEndDay) {
        setSelectedEndDay(pickerEndDay ?? nextDay);
      }
    },
    [pickerEndDay, selectedEndDay],
  );
  const handleEndDayChange = useMemo(
    () => (nextDay: string | null) => {
      setSelectedEndDay(nextDay);
      if (!nextDay) {
        setSelectedStartDay(null);
        return;
      }
      if (!selectedStartDay) {
        setSelectedStartDay(pickerStartDay ?? nextDay);
      }
    },
    [pickerStartDay, selectedStartDay],
  );
  const summaryInput = useMemo(
    () => ({
      page: 1,
      pageSize: 1,
      sorts: [],
      filters: suiteExecutionFilters,
    }),
    [suiteExecutionFilters],
  );
  const summaryFiltersKey = useMemo(
    () => JSON.stringify(summaryInput.filters),
    [summaryInput.filters],
  );
  const currentEarliestQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'suite-executions/current-earliest',
      summaryFiltersKey,
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
    ),
    enabled: queryEnabled,
    queryFn: async () =>
      await querySuiteExecutions(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'timestamp', direction: 'asc' }],
          filters: suiteExecutionFilters,
        },
        accessToken,
      ),
  });
  const currentLatestQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'suite-executions/current-latest',
      summaryFiltersKey,
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
    ),
    enabled: queryEnabled,
    queryFn: async () =>
      await querySuiteExecutions(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'timestamp', direction: 'desc' }],
          filters: suiteExecutionFilters,
        },
        accessToken,
      ),
  });
  const suiteSummaryQuery = useQuery<SuiteExecutionStatusSummaryDto>({
    queryKey: queryKeys.suiteExecutionSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      summaryFiltersKey,
    ),
    enabled: queryEnabled,
    queryFn: async () =>
      await querySuiteExecutionStatusSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        summaryInput,
        accessToken,
      ),
  });
  const suiteSummary = suiteSummaryQuery.data ?? null;
  const showStatusDelta = Boolean(
    !isAllMode && !hasCustomDates && previousRangeStartIso && previousRangeEndIso,
  );
  const previousSummaryFilters = useMemo(
    () => mergeFilters(baseSuiteFilters, previousDateRangeFilters, tagFilters),
    [baseSuiteFilters, previousDateRangeFilters, tagFilters],
  );
  const previousSummaryInput = useMemo(
    () =>
      showStatusDelta
        ? {
            page: 1,
            pageSize: 1,
            sorts: [],
            filters: previousSummaryFilters,
          }
        : null,
    [previousSummaryFilters, showStatusDelta],
  );
  const previousSummaryFiltersKey = useMemo(
    () => (previousSummaryInput ? JSON.stringify(previousSummaryInput.filters) : 'latest'),
    [previousSummaryInput],
  );
  const previousEarliestQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'suite-executions/previous-earliest',
      previousSummaryFiltersKey,
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
    ),
    enabled: Boolean(queryEnabled && previousSummaryInput),
    queryFn: async () =>
      await querySuiteExecutions(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'timestamp', direction: 'asc' }],
          filters: previousSummaryFilters,
        },
        accessToken,
      ),
  });
  const previousLatestQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'suite-executions/previous-latest',
      previousSummaryFiltersKey,
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
    ),
    enabled: Boolean(queryEnabled && previousSummaryInput),
    queryFn: async () =>
      await querySuiteExecutions(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'timestamp', direction: 'desc' }],
          filters: previousSummaryFilters,
        },
        accessToken,
      ),
  });
  const previousSuiteSummaryQuery = useQuery<SuiteExecutionStatusSummaryDto>({
    queryKey: queryKeys.suiteExecutionSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      previousSummaryFiltersKey,
    ),
    enabled: Boolean(queryEnabled && previousSummaryInput),
    queryFn: async () =>
      await querySuiteExecutionStatusSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        previousSummaryInput ?? { page: 1, pageSize: 1, sorts: [], filters: [] },
        accessToken,
      ),
  });
  const previousSuiteSummary = showStatusDelta ? (previousSuiteSummaryQuery.data ?? null) : null;
  const suiteCounts = useMemo(() => buildSuiteCounts(suiteSummary), [suiteSummary]);
  const previousSuiteCounts = useMemo(
    () => buildSuiteCounts(previousSuiteSummary),
    [previousSuiteSummary],
  );
  const suiteDeltas = useMemo(
    () =>
      showStatusDelta && previousSuiteSummary
        ? buildStatusDeltaMap(suiteCounts, previousSuiteCounts)
        : undefined,
    [previousSuiteCounts, previousSuiteSummary, showStatusDelta, suiteCounts],
  );
  const suiteOptions = useMemo(
    () => mapStatusOptionsToMetricOptions(suiteExecutionSummaryStatusOptions, resolveMetricTone),
    [],
  );
  const totalCount = useMemo(
    () =>
      Object.values(suiteCounts).reduce(
        (total, value) => total + (typeof value === 'number' ? value : 0),
        0,
      ),
    [suiteCounts],
  );
  const passRateOverride = useMemo(() => resolvePassRateFromSummary(suiteSummary), [suiteSummary]);
  const previousPassRateOverride = useMemo(
    () => resolvePassRateFromSummary(previousSuiteSummary),
    [previousSuiteSummary],
  );
  const suiteMetricCards = useMemo(
    () =>
      buildStatusMetricCards({
        options: suiteOptions,
        counts: suiteCounts,
        deltas: suiteDeltas,
        total: totalCount,
        loading: suiteSummaryQuery.isLoading,
        showDelta: showStatusDelta,
      }),
    [
      showStatusDelta,
      suiteCounts,
      suiteDeltas,
      suiteOptions,
      suiteSummaryQuery.isLoading,
      totalCount,
    ],
  );
  const currentFrequency = useMemo(() => {
    const earliestItems = currentEarliestQuery.data ? currentEarliestQuery.data.items : [];
    const latestItems = currentLatestQuery.data ? currentLatestQuery.data.items : [];
    return resolveFrequencyPerDay(
      suiteSummary?.total ?? null,
      resolveTimestamp(earliestItems[0] ? earliestItems[0].timestamp : null),
      resolveTimestamp(latestItems[0] ? latestItems[0].timestamp : null),
    );
  }, [currentEarliestQuery.data, currentLatestQuery.data, suiteSummary?.total]);
  const previousFrequency = useMemo(
    () =>
      showMetricDelta
        ? resolveFrequencyPerDay(
            previousSuiteSummary?.total ?? null,
            resolveTimestamp(
              previousEarliestQuery.data?.items[0]
                ? previousEarliestQuery.data.items[0].timestamp
                : null,
            ),
            resolveTimestamp(
              previousLatestQuery.data?.items[0]
                ? previousLatestQuery.data.items[0].timestamp
                : null,
            ),
          )
        : null,
    [
      previousEarliestQuery.data,
      previousLatestQuery.data,
      previousSuiteSummary?.total,
      showMetricDelta,
    ],
  );
  const metricValueItems = useMemo(
    () =>
      buildMetricValueItems({
        metrics: metricsQuery.data ?? null,
        previousMetrics: showMetricDelta ? (previousMetricsQuery.data ?? null) : null,
        thresholds: thresholds.suites,
        frequencyOverride: currentFrequency,
        previousFrequencyOverride: previousFrequency,
        passRateOverride,
        previousPassRateOverride,
        showDelta: showMetricDelta,
      }),
    [
      currentFrequency,
      metricsQuery.data,
      passRateOverride,
      previousFrequency,
      previousMetricsQuery.data,
      previousPassRateOverride,
      showMetricDelta,
      thresholds.suites,
    ],
  );
  const chartHeight = 360;
  const chartDates = useMemo(() => {
    const labels = metricsQuery.data?.metricsRangeLabels ?? [];
    const dates = metricsQuery.data?.metricsRangeDates ?? [];
    return buildChartDates(labels, dates);
  }, [metricsQuery.data?.metricsRangeDates, metricsQuery.data?.metricsRangeLabels]);
  const lineChartLabels = metricsQuery.data?.metricsRangeLabels ?? [];
  const stackedChartData = useMemo(
    () => buildStackedExecutionChartData(metricsQuery.data),
    [metricsQuery.data],
  );
  const passRateChartData = useMemo(() => {
    const series = buildLineChartSeries(
      metricsQuery.data?.series.filteredPassRate,
      lineChartLabels,
      chartDates,
      'passRate',
    );
    if (series) return series;
    return metricsQuery.isFetched ? [] : null;
  }, [
    chartDates,
    lineChartLabels,
    metricsQuery.data?.series.filteredPassRate,
    metricsQuery.isFetched,
  ]);
  const frequencyChartData = useMemo(() => {
    const series = buildLineChartSeries(
      metricsQuery.data?.series.executionTrendMetrics,
      lineChartLabels,
      chartDates,
      'frequency',
    );
    if (series) return series;
    return metricsQuery.isFetched ? [] : null;
  }, [
    chartDates,
    lineChartLabels,
    metricsQuery.data?.series.executionTrendMetrics,
    metricsQuery.isFetched,
  ]);
  const durationChartData = useMemo(() => {
    const series = buildLineChartSeries(
      metricsQuery.data?.series.executionDurationAverage,
      lineChartLabels,
      chartDates,
      'duration',
    );
    if (series) return series;
    return metricsQuery.isFetched ? [] : null;
  }, [
    chartDates,
    lineChartLabels,
    metricsQuery.data?.series.executionDurationAverage,
    metricsQuery.isFetched,
  ]);
  const handleChartSelect = useCallback(
    (dataIndex: number) => {
      const day = chartDates[dataIndex];
      const nextSelection = resolveChartDaySelection(selectedStartDay, selectedEndDay, day);
      setSelectedStartDay(nextSelection.startDay);
      setSelectedEndDay(nextSelection.endDay);
    },
    [chartDates, selectedEndDay, selectedStartDay],
  );
  const showLineChartPoints = Boolean(
    selectedStartDay && selectedEndDay && selectedStartDay === selectedEndDay,
  );
  const isLatestMode = range === 'latest';
  const isDeltaToggleDisabled = isLatestMode || isAllMode;
  const showTableDeltaToggle = !isDeltaToggleDisabled;
  const showTableDeltas = tableDeltasEnabled && !isDeltaToggleDisabled;
  const showChartDateClear = Boolean(selectedStartDay ?? selectedEndDay);
  const handleChartDateClear = useCallback(() => {
    setSelectedStartDay(null);
    setSelectedEndDay(null);
  }, []);
  const passRateGuideLines = useMemo(
    () => buildThresholdGuideLines(thresholds.suites.passRate),
    [thresholds.suites.passRate],
  );
  const frequencyGuideLines = useMemo(
    () => buildThresholdGuideLines(thresholds.suites.frequency),
    [thresholds.suites.frequency],
  );
  const durationGuideLines = useMemo(
    () => buildThresholdGuideLines(thresholds.suites.avgDuration),
    [thresholds.suites.avgDuration],
  );

  useEffect(() => {
    setSuppressChartAnimation(true);
    const timer = window.setTimeout(() => {
      setSuppressChartAnimation(false);
    }, 0);
    return () => {
      window.clearTimeout(timer);
    };
  }, [chartTab]);

  return {
    title,
    suiteId,
    chartTab,
    setChartTab,
    suppressChartAnimation,
    range,
    setRange,
    selectedStartDay,
    setSelectedStartDay,
    selectedEndDay,
    setSelectedEndDay,
    isAllMode,
    pickerStartDay,
    pickerEndDay,
    handleStartDayChange,
    handleEndDayChange,
    suiteMetricCards,
    metricValueItems,
    chartHeight,
    showChartDateClear,
    handleChartDateClear,
    stackedChartData,
    passRateChartData,
    frequencyChartData,
    durationChartData,
    passRateGuideLines,
    frequencyGuideLines,
    durationGuideLines,
    handleChartSelect,
    showLineChartPoints,
    dataTab,
    setDataTab,
    tableDeltasEnabled,
    setTableDeltasEnabled,
    showTableDeltaToggle,
    showTableDeltas,
    selectedTags,
    tagOptions,
    tagOptionsLoading,
    handleTagsChange: setSelectedTags,
    suiteExecutionFilters,
  };
};

export const useSuiteDetailPageViewModel = (
  params: SuiteDetailPageViewModelParams,
): SuiteDetailPageViewModel => useSuiteDetailPageViewModelInternal(params);
